/**
 * Dialog genérico para exibir mensagem ao usuário (erro, aviso, informação).
 * Usado para NO_FILL, erro genérico de mídia e outros casos.
 */

import { ButtonComponent } from './button.component';
import { logger } from '../utils/logger.util';

export interface MessageDialogOptions {
  /** Mensagem exibida no dialog */
  message: string;
  /** Texto do botão (default: "Entendi") */
  buttonText?: string;
}

export class MessageDialogComponent {
  private overlay: HTMLElement | null = null;

  /**
   * Exibe o dialog com a mensagem informada.
   */
  static show(options: MessageDialogOptions): MessageDialogComponent {
    const dialog = new MessageDialogComponent();
    dialog.open(options);
    return dialog;
  }

  private open(options: MessageDialogOptions): void {
    this.createDialog(options);
    this.attachEvents();
  }

  private createDialog(options: MessageDialogOptions): void {
    const { message, buttonText = 'Entendi' } = options;
    logger.debug('[MessageDialogComponent] Exibindo dialog:', { message: message.substring(0, 50) + '...' });

    this.overlay = document.createElement('div');
    this.overlay.className = 'message-dialog-overlay';

    this.overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 99999;
    `;

    const dialogContent = document.createElement('div');
    dialogContent.className = 'message-dialog';
    dialogContent.style.cssText = `
      background-color: white;
      border-radius: 16px;
      padding: 40px 32px;
      display: flex;
      flex-direction: column;
      align-items: center;
      min-width: 280px;
      max-width: 320px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    `;

    const escapedMessage = this.escapeHtml(message);
    dialogContent.innerHTML = `
      <p class="message-dialog-text" style="
        font-size: 16px;
        color: rgba(0, 0, 0, 0.87);
        font-weight: 400;
        text-align: center;
        margin: 0 0 24px 0;
        line-height: 1.5;
      ">
        ${escapedMessage}
      </p>
      <div id="message-dialog-button-container"></div>
    `;

    const buttonContainer = dialogContent.querySelector('#message-dialog-button-container');
    if (buttonContainer) {
      const okButton = ButtonComponent.createPrimaryButton({
        text: buttonText,
        id: 'message-dialog-ok-button',
        fullWidth: true,
        onClick: () => this.close()
      });
      buttonContainer.appendChild(okButton);
    }

    this.overlay.appendChild(dialogContent);

    // Fechar ao clicar no overlay (fora do card)
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.close();
      }
    });

    // Evitar que o clique no card feche o overlay
    dialogContent.addEventListener('click', (e) => e.stopPropagation());

    document.body.appendChild(this.overlay);
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private attachEvents(): void {
    document.addEventListener('keydown', this.handleKeydown);
  }

  private handleKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
    }
  };

  close(): void {
    document.removeEventListener('keydown', this.handleKeydown);
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
      logger.debug('[MessageDialogComponent] Dialog fechado');
    }
  }
}
